import { formatCurrency } from '@crm/shared';
import { cn, Modal } from '@crm/ui';
import { Info } from 'lucide-react';
import { useState } from 'react';
import type { FunnelRow } from '../api';

/**
 * As quatro etapas de valor. Nao sao as cinco do funil: ali se acompanha a
 * mecanica do atendimento - quem recebeu disparo, quem respondeu, quem falou
 * com o bot, quem falou com gente. Aqui se acompanha dinheiro, e para isso
 * bastam quatro degraus.
 *
 * `stage` e a etapa do funil que alimenta o degrau; `SCHEDULED` no ultimo e
 * so um destino de clique - o numero dele vem do comparecimento.
 */
const DEGRAUS = [
  {
    key: 'MESSAGE_SENT',
    label: 'Prospeccao',
    cor: 'from-amber-400 to-amber-500',
    dica: 'Recebeu o disparo',
  },
  { key: 'LEAD', label: 'Qualificacao', cor: 'from-sky-400 to-sky-500', dica: 'Virou lead' },
  {
    key: 'SCHEDULED',
    label: 'Agendamento',
    cor: 'from-indigo-400 to-indigo-500',
    dica: 'Marcou horario',
  },
  {
    key: 'SCHEDULED',
    label: 'Comparecimento',
    cor: 'from-emerald-400 to-emerald-500',
    dica: 'Atendimento concluido',
  },
] as const;

/**
 * Quanto dinheiro esta parado em cada degrau do funil.
 *
 * O lead nao tem valor gravado - ninguem digita "esta negociacao vale X" neste
 * CRM. Entao o valor e uma estimativa honesta: quantos leads estao no degrau,
 * vezes o ticket medio do periodo. E o que entra se cada um daqueles contatos
 * virar um atendimento comum.
 *
 * A barra e proporcional ao valor, nao ao formato de funil: aqui se compara
 * tamanho de dinheiro, e barra estilizada esconderia a diferenca.
 */
export function PipelineChart({
  rows,
  attended,
  averageTicket,
  onSelectStage,
}: {
  rows: FunnelRow[];
  /** Leads do mesmo recorte que chegaram a ser atendidos. */
  attended: number;
  /** Ticket medio do mesmo recorte dos filtros. */
  averageTicket: number;
  onSelectStage?: (stage: string) => void;
}) {
  const [explicando, setExplicando] = useState<(typeof DEGRAUS)[number] | null>(null);
  const porEtapa = new Map(rows.map((row) => [row.stage, row.total]));

  const degraus = DEGRAUS.map((degrau, indice) => {
    // O ultimo degrau e desfecho, nao etapa: vem do atendimento concluido.
    const leads = indice === DEGRAUS.length - 1 ? attended : (porEtapa.get(degrau.key) ?? 0);

    return { ...degrau, leads, valor: leads * averageTicket };
  });

  // A maior barra ocupa a linha inteira; as outras se medem por ela.
  const maior = Math.max(...degraus.map((degrau) => degrau.valor), 1);

  return (
    <div className="space-y-4">
      <div className="space-y-2.5">
        {degraus.map((degrau) => {
          const largura = Math.max((degrau.valor / maior) * 100, degrau.valor > 0 ? 4 : 0);
          const Elemento = onSelectStage ? 'button' : 'div';

          return (
            /* A linha deixou de ser um botao so: o simbolo de informacao mora
               dentro dela, e botao dentro de botao nao vale. Quem leva ao
               quadro agora e a barra. */
            <div
              key={degrau.label}
              className="grid w-full grid-cols-[minmax(0,7rem)_1fr_auto_auto] items-center gap-3 px-1 py-1 text-left"
            >
              <span className="truncate text-sm text-muted-foreground">{degrau.label}</span>

              <Elemento
                {...(onSelectStage
                  ? { type: 'button' as const, onClick: () => onSelectStage(degrau.key) }
                  : {})}
                title={degrau.dica}
                className={cn(
                  'block h-6 w-full overflow-hidden rounded-md bg-muted/70',
                  onSelectStage ? 'transition-opacity hover:opacity-90' : '',
                )}
              >
                <span
                  className={cn('block h-full rounded-md bg-gradient-to-r', degrau.cor)}
                  style={{ width: `${largura}%` }}
                />
              </Elemento>

              <span className="text-right text-sm font-medium tabular-nums">
                {formatCurrency(degrau.valor)}
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {degrau.leads}
                </span>
              </span>

              <button
                type="button"
                aria-label={`O que e ${degrau.label}`}
                onClick={() => setExplicando(degrau)}
                className="text-muted-foreground transition-colors hover:text-foreground"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      <Modal
        open={Boolean(explicando)}
        onClose={() => setExplicando(null)}
        title={explicando?.label ?? ''}
        size="small"
        actions={[{ label: 'Entendi', onClick: () => setExplicando(null) }]}
      >
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>{explicando?.dica}</p>
          <p>
            O valor e estimado, nao fechado: nenhum lead tem preco gravado neste CRM. Vale quantos
            leads estao no degrau vezes o ticket medio do periodo, hoje em{' '}
            {formatCurrency(averageTicket)}.
          </p>
          {explicando?.label === 'Comparecimento' ? (
            <p>
              Este e o unico degrau que virou dinheiro de verdade: sai do atendimento ja concluido,
              nao de uma promessa de agenda.
            </p>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
